import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  BookOpen,
  Clock3,
  Users,
  ShieldCheck,
  CheckCircle2,
  Award,
  Play,
  ArrowRight,
  ChevronRight,
  Target,
  Layers,
  Sparkles,
  MessageCircle,
  AlertCircle,
  Compass,
  Lightbulb,
  TrendingUp,
  Zap,
  Rocket,
} from "lucide-react";
import { Header, Footer, CourseCard, Loading } from "../components/Shell";
import { api, LOGO } from "../lib/api";
import { useSeo, jsonLdCourse, jsonLdFaq } from "../lib/seo";
import { useSiteContent, rich } from "../lib/siteContent";

const ExtLink = ({ to, children, ...rest }) => (/^https?:/.test(to || "") ? <a href={to} target="_blank" rel="noreferrer" {...rest}>{children}</a> : <Link to={to || "/"} {...rest}>{children}</Link>);

export function Home() {
  const [items, setItems] = useState([]);
  const [settings, setSettings] = useState({});
  const [paths, setPaths] = useState([]);
  const { t, list, raw } = useSiteContent("home");
  useSeo({
    title: "Digital Marketing Academy",
    description:
      "MASTOFA.COM — belajar Meta Ads, TikTok Ads, dan Shopee Ads dengan kursus praktis, mentoring Privat 1-on-1, VIP Member Telegram, dan Jasa Iklan penuh pendampingan.",
    path: "/",
  });
  useEffect(() => {
    // Capture ?ref= into localStorage so it survives navigation
    const url = new URL(window.location.href);
    const ref = url.searchParams.get("ref");
    if (ref) localStorage.setItem("mastofa_ref", ref.toUpperCase());
    api("/courses").then(setItems).catch(() => {});
    api("/settings/public").then(setSettings).catch(() => {});
    api("/learning-paths").then((d) => setPaths(d.levels || [])).catch(() => {});
  }, []);
  return (
    <div>
      <Header />
      <main>
        <section className="hero">
          <div className="hero-copy reveal">
            <div className="eyebrow">
              <span className="eyebrow-dot" /> {t("hero.eyebrow")}{" "}
              <span className="line" />
            </div>
            <h1>
              {raw("hero.title") || settings.hero_title || t("hero.title")}
              <br />
              <em>{raw("hero.subtitle") || settings.hero_subtitle || t("hero.subtitle")}</em>
            </h1>
            <p>
              {raw("hero.body") || settings.hero_body || t("hero.body")}
            </p>
            <div className="hero-actions">
              <Link
                to={t("hero.cta1_to")}
                className="button"
                data-testid="hero-start-button"
              >
                {t("hero.cta1")} <ArrowRight size={18} />
              </Link>
              <Link
                to={t("hero.cta2_to")}
                className="button-ghost"
                data-testid="hero-courses-button"
              >
                {t("hero.cta2")} <ChevronRight size={18} />
              </Link>
            </div>
            <div className="trust-row">
              <ShieldCheck size={18} /> {t("hero.trust1")}{" "}
              <span /> <CheckCircle2 size={18} /> {t("hero.trust2")}{" "}
              <span /> <Award size={18} /> {t("hero.trust3")}
            </div>
          </div>
          <div className="hero-visual reveal-delay">
            <div className="visual-frame">
              <img
                src={raw("hero.image") || settings.hero_image_url || t("hero.image")}
                alt="Belajar strategi digital marketing"
              />
              <div className="visual-caption">
                <span className="caption-number">01</span>
                <span>{rich(t("hero.caption"), "b")}</span>
              </div>
            </div>
            <div className="stat-card">
              <strong>{items.length || 13}</strong>
              <span>{rich(t("hero.stat_label"))}</span>
            </div>
          </div>
        </section>

        {/* --- PRIVAT 1-on-1 PROMINENT FEATURE (Produk Utama) --- */}
        <section className="section privat-featured" data-testid="home-privat-featured" style={{
          background: "linear-gradient(135deg,#0f172a 0%,#1e40af 60%,#3b82f6 100%)",
          color: "#f8fafc",
          padding: "72px 32px",
          borderRadius: 20,
          margin: "40px 0",
          position: "relative",
          overflow: "hidden",
        }}>
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1.4fr) minmax(0,1fr)", gap: 48, alignItems: "center" }} className="privat-featured-grid">
            <div>
              <span className="section-kicker" style={{ color: "#fbbf24" }}>{t("privat.kicker")}</span>
              <h2 style={{ fontSize: "2.6rem", lineHeight: 1.1, marginTop: 16, color: "#fff" }}>
                {rich(t("privat.title"), "span", { style: { color: "#fbbf24" } })}
              </h2>
              <p style={{ fontSize: "1.05rem", opacity: 0.9, marginTop: 20, maxWidth: 560, lineHeight: 1.7 }}>
                {t("privat.body")}
              </p>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 28, maxWidth: 520 }}>
                <div style={{ background: "rgba(255,255,255,0.1)", padding: 16, borderRadius: 12, backdropFilter: "blur(6px)" }}>
                  <div style={{ fontSize: 12, opacity: 0.75, textTransform: "uppercase", letterSpacing: 1 }}>{t("privat.ind_label")}</div>
                  <div style={{ fontSize: "1.4rem", fontWeight: 700, marginTop: 4 }}>{t("privat.ind_price")}</div>
                  <small style={{ opacity: 0.7 }}>{t("privat.ind_sub")}</small>
                </div>
                <div style={{ background: "rgba(251,191,36,0.15)", padding: 16, borderRadius: 12, border: "1px solid rgba(251,191,36,0.4)" }}>
                  <div style={{ fontSize: 12, color: "#fbbf24", textTransform: "uppercase", letterSpacing: 1 }}>{t("privat.corp_label")}</div>
                  <div style={{ fontSize: "1.4rem", fontWeight: 700, marginTop: 4 }}>{t("privat.corp_price")}</div>
                  <small style={{ opacity: 0.7 }}>{t("privat.corp_sub")}</small>
                </div>
              </div>
              <div style={{ display: "flex", gap: 12, marginTop: 32, flexWrap: "wrap" }}>
                <Link to={t("privat.cta1_to")} className="button" data-testid="home-privat-book-cta" style={{
                  background: "#fbbf24",
                  color: "#0f172a",
                  fontWeight: 700,
                  padding: "14px 28px",
                  fontSize: "1rem",
                }}>
                  {t("privat.cta1")} <ArrowRight size={18} />
                </Link>
                <Link to={t("privat.cta2_to")} className="button-ghost" data-testid="home-privat-price-cta" style={{
                  background: "transparent",
                  color: "#fff",
                  border: "1px solid rgba(255,255,255,0.4)",
                  padding: "14px 28px",
                }}>
                  {t("privat.cta2")}
                </Link>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 24, fontSize: 13, opacity: 0.85, flexWrap: "wrap" }}>
                {list("privat.badges").map((b, i) => (
                  <span key={i}><CheckCircle2 size={14} style={{ verticalAlign: "middle", marginRight: 4 }} /> {b.text}</span>
                ))}
              </div>
            </div>
            <div style={{ display: "grid", gap: 12 }}>
              {list("privat.features").map((f, i) => (
                <div key={i} style={{
                  background: "rgba(255,255,255,0.08)",
                  padding: 18,
                  borderRadius: 12,
                  backdropFilter: "blur(8px)",
                  border: "1px solid rgba(255,255,255,0.1)",
                }}>
                  <div style={{ fontSize: 28, marginBottom: 8 }}>{f.icon}</div>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>{f.title}</div>
                  <small style={{ opacity: 0.8, lineHeight: 1.5, display: "block" }}>{f.body}</small>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* --- SECTION: MASALAH — "Kenapa banyak orang gagal jalankan iklan" --- */}
        <section className="section masalah-section" data-testid="home-masalah" style={{
          background: "#f8fafc", padding: "72px 32px", borderRadius: 20, margin: "40px 0",
        }}>
          <div style={{ textAlign: "center", maxWidth: 720, margin: "0 auto 40px" }}>
            <span className="section-kicker" style={{ color: "#dc2626" }}>{t("masalah.kicker")}</span>
            <h2 style={{ fontSize: "2rem", marginTop: 12, color: "#0f172a" }}>
              {rich(t("masalah.title"), "span", { style: { color: "#dc2626" } })}
            </h2>
          </div>
          <div style={{
            display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))",
            gap: 20, maxWidth: 1100, margin: "0 auto",
          }}>
            {list("masalah.items").map((m, i) => (
              <div key={i} style={{
                background: "#fff", padding: 24, borderRadius: 12,
                border: "1px solid #e2e8f0",
              }}>
                <div style={{ color: "#dc2626", marginBottom: 12 }}><AlertCircle size={22} /></div>
                <b style={{ display: "block", marginBottom: 8, color: "#0f172a" }}>{m.title}</b>
                <small style={{ color: "#475569", lineHeight: 1.5 }}>{m.body}</small>
              </div>
            ))}
          </div>
        </section>

        {/* --- SECTION: SOLUSI + CARA BELAJAR — 6-step method --- */}
        <section className="section solusi-section" data-testid="home-solusi" style={{ padding: "72px 32px" }}>
          <div style={{ textAlign: "center", maxWidth: 720, margin: "0 auto 48px" }}>
            <span className="section-kicker">{t("solusi.kicker")}</span>
            <h2 style={{ fontSize: "2rem", marginTop: 12 }}>
              {rich(t("solusi.title"))}
            </h2>
            <p style={{ color: "#475569", marginTop: 16, lineHeight: 1.6 }}>
              {t("solusi.body")}
            </p>
          </div>
          <div style={{
            display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))",
            gap: 16, maxWidth: 1200, margin: "0 auto",
          }}>
            {list("solusi.steps").map((s0, i) => {
              const icons = [<Compass size={20} />, <Lightbulb size={20} />, <Target size={20} />, <BookOpen size={20} />, <Zap size={20} />, <Rocket size={20} />];
              const s = { ...s0, n: String(i + 1), icon: icons[i % icons.length] };
              return (
              <div key={i} data-testid={`step-${s.n}`} style={{
                background: "#fff", padding: 20, borderRadius: 12,
                border: "1px solid #e2e8f0", textAlign: "center",
                position: "relative",
              }}>
                <div style={{
                  position: "absolute", top: -12, left: "50%", transform: "translateX(-50%)",
                  background: "#2563eb", color: "#fff", borderRadius: "50%",
                  width: 28, height: 28, display: "flex", alignItems: "center",
                  justifyContent: "center", fontWeight: 700, fontSize: 13,
                }}>{s.n}</div>
                <div style={{ color: "#2563eb", margin: "8px 0 12px", display: "flex", justifyContent: "center" }}>{s.icon}</div>
                <b style={{ display: "block", marginBottom: 6, fontSize: "0.95rem" }}>{s.t}</b>
                <small style={{ color: "#64748b", lineHeight: 1.4, fontSize: "0.8rem" }}>{s.d}</small>
              </div>
              );
            })}
          </div>
        </section>

        {/* --- SECTION: PILIH KEBUTUHAN ANDA (paling penting per spec) --- */}
        <section className="section pilih-kebutuhan" data-testid="home-pilih-kebutuhan" style={{
          background: "linear-gradient(180deg,#0f172a,#1e293b)",
          color: "#f8fafc", padding: "80px 32px", borderRadius: 20, margin: "40px 0",
        }}>
          <div style={{ textAlign: "center", maxWidth: 720, margin: "0 auto 48px" }}>
            <span className="section-kicker" style={{ color: "#fbbf24" }}>{t("pilih.kicker")}</span>
            <h2 style={{ fontSize: "2.2rem", marginTop: 12, color: "#fff" }}>
              {rich(t("pilih.title"), "span", { style: { color: "#fbbf24" } })}
            </h2>
            <p style={{ color: "#cbd5e1", marginTop: 16, lineHeight: 1.6 }}>
              {t("pilih.body")}
            </p>
          </div>
          <div style={{
            display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))",
            gap: 16, maxWidth: 1200, margin: "0 auto",
          }}>
            {list("pilih.items").map((p0, i) => {
              const tids = ["pilih-pemula", "pilih-shopee", "pilih-tiktok", "pilih-meta", "pilih-advanced", "pilih-privat"];
              const p = { ...p0, testid: tids[i] || `pilih-${i}` };
              return (
              <ExtLink key={i} to={p.target} data-testid={p.testid} style={{
                background: p.highlight ? "rgba(251,191,36,0.12)" : "rgba(255,255,255,0.06)",
                border: p.highlight ? "1px solid rgba(251,191,36,0.4)" : "1px solid rgba(255,255,255,0.1)",
                padding: 24, borderRadius: 14, color: "#f8fafc",
                textDecoration: "none", transition: "all 0.15s ease", display: "block",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.background = p.highlight ? "rgba(251,191,36,0.2)" : "rgba(255,255,255,0.12)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.background = p.highlight ? "rgba(251,191,36,0.12)" : "rgba(255,255,255,0.06)"; }}
              >
                <div style={{ fontSize: 32, marginBottom: 12 }}>{p.emoji}</div>
                <b style={{ display: "block", marginBottom: 8, fontSize: "1.05rem" }}>{p.title}</b>
                <p style={{ color: "#cbd5e1", fontSize: "0.85rem", lineHeight: 1.5, marginBottom: 16 }}>{p.body}</p>
                <span style={{
                  color: p.highlight ? "#fbbf24" : "#60a5fa",
                  fontSize: "0.85rem", fontWeight: 600, display: "inline-flex",
                  alignItems: "center", gap: 6,
                }}>
                  → {p.label} <ArrowRight size={14} />
                </span>
              </ExtLink>
              );
            })}
          </div>
          <p style={{ textAlign: "center", color: "#94a3b8", marginTop: 32, fontSize: "0.9rem" }}>
            {t("pilih.note")}{" "}
            <a href={t("pilih.note_to")}
               target="_blank" rel="noreferrer"
               style={{ color: "#fbbf24", textDecoration: "underline" }}
               data-testid="pilih-wa-consult">
              {t("pilih.note_link")}
            </a>
          </p>
        </section>

        {/* --- SECTION: LEARNING PATH PREVIEW (Level 0-4) --- */}
        <section className="section learning-path-preview" data-testid="home-learning-path" style={{ padding: "72px 32px" }}>
          <div style={{ textAlign: "center", maxWidth: 720, margin: "0 auto 40px" }}>
            <span className="section-kicker">{t("path.kicker")}</span>
            <h2 style={{ fontSize: "2rem", marginTop: 12 }}>
              {rich(t("path.title"))}
            </h2>
            <p style={{ color: "#475569", marginTop: 16 }}>
              {t("path.body")}
            </p>
          </div>
          <div style={{ maxWidth: 900, margin: "0 auto", display: "flex", flexDirection: "column", gap: 12 }}>
            {paths.map((lvl) => (
              <Link
                key={lvl.level}
                to={`/belajar#level-${lvl.level}`}
                data-testid={`lp-lvl-${lvl.level}`}
                style={{
                  background: "#fff", border: `2px solid ${lvl.color}22`,
                  borderLeft: `6px solid ${lvl.color}`, borderRadius: 12,
                  padding: "20px 24px", textDecoration: "none", color: "#0f172a",
                  display: "grid", gridTemplateColumns: "auto 1fr auto", gap: 20,
                  alignItems: "center", transition: "all 0.15s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "translateX(4px)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = "translateX(0)"; }}
              >
                <div style={{
                  background: lvl.color, color: "#fff", borderRadius: "50%",
                  width: 48, height: 48, display: "flex", alignItems: "center",
                  justifyContent: "center", fontSize: 20, fontWeight: 800,
                }}>
                  {lvl.level}
                </div>
                <div>
                  <b style={{ fontSize: "1.05rem", display: "block", marginBottom: 2 }}>
                    {lvl.title.replace(/^Level \d+ — /, "")}
                  </b>
                  <small style={{ color: "#64748b" }}>
                    {lvl.tagline} · {lvl.total_courses > 0 ? `${lvl.total_courses} kursus tersedia` : "Segera hadir"}
                  </small>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#64748b" }}>
                  {lvl.total_courses > 0 ? "Lihat kursus" : "Coming soon"} <ArrowRight size={16} />
                </div>
              </Link>
            ))}
          </div>
        </section>

        <section className="section intro-band" style={{ display: "none" }}>
          <div><span /></div>
        </section>

        <section
          className="section level4-highlight"
          data-testid="home-level4-highlight"
          style={{ padding: "56px 32px 24px" }}
        >
          <div
            className="level4-highlight-card"
            style={{
              maxWidth: 1000,
              margin: "0 auto",
              background: "linear-gradient(135deg,#7f1d1d 0%,#ef4444 100%)",
              borderRadius: 16,
              padding: "44px 40px",
              color: "#fff",
              display: "grid",
              gridTemplateColumns: "1.35fr 1fr",
              gap: 40,
              alignItems: "center",
              boxShadow: "0 20px 44px rgba(239,68,68,0.24)",
            }}
          >
            <div>
              <span
                style={{
                  display: "inline-block",
                  background: "rgba(255,255,255,0.18)",
                  padding: "5px 12px",
                  borderRadius: 999,
                  fontSize: 11,
                  letterSpacing: "0.14em",
                  fontWeight: 800,
                  marginBottom: 14,
                }}
              >
                {t("level4.badge")}
              </span>
              <h2
                style={{
                  fontSize: "clamp(1.5rem, 2.4vw, 2rem)",
                  lineHeight: 1.15,
                  margin: "0 0 12px",
                  fontWeight: 800,
                  letterSpacing: "-0.02em",
                  color: "#fff",
                }}
              >
                {rich(t("level4.title"), "em", { style: { color: "#fde047", fontStyle: "normal" } })}
              </h2>
              <p
                style={{
                  margin: "0 0 24px",
                  color: "rgba(255,255,255,0.9)",
                  lineHeight: 1.65,
                  fontSize: "0.98rem",
                  maxWidth: 520,
                }}
              >
                {t("level4.body")}
              </p>
              <Link
                to={t("level4.cta_to")}
                data-testid="home-level4-cta"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 10,
                  background: "#fff",
                  color: "#7f1d1d",
                  padding: "13px 22px",
                  borderRadius: 8,
                  fontWeight: 800,
                  fontSize: "0.95rem",
                  textDecoration: "none",
                  boxShadow: "0 8px 18px rgba(0,0,0,0.16)",
                }}
              >
                {t("level4.cta")} <ArrowRight size={17} />
              </Link>
              <small
                style={{
                  display: "block",
                  marginTop: 14,
                  color: "rgba(255,255,255,0.72)",
                  fontSize: 12,
                }}
              >
                {t("level4.note")}
              </small>
            </div>
            <ul
              style={{
                display: "grid",
                gap: 10,
                margin: 0,
                padding: 0,
                listStyle: "none",
                fontSize: "0.92rem",
                color: "#fff",
              }}
              data-testid="home-level4-topics"
            >
              {list("level4.topics").map((t4) => (
                <li
                  key={t4.label}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    background: "rgba(255,255,255,0.1)",
                    padding: "10px 14px",
                    borderRadius: 8,
                    border: "1px solid rgba(255,255,255,0.14)",
                  }}
                >
                  <span aria-hidden style={{ fontSize: "1.05rem" }}>
                    {t4.icon}
                  </span>
                  <b style={{ fontWeight: 700 }}>{t4.label}</b>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="section home-services">
          <div className="section-head">
            <div>
              <span className="section-kicker">{t("services.kicker")}</span>
              <h2>{rich(t("services.title"))}</h2>
            </div>
            <Link to={t("services.link_to")} className="text-link" data-testid="home-services-more">
              {t("services.link")} <ArrowRight size={17} />
            </Link>
          </div>
          <div className="home-services-grid">
            {list("services.items").map((s, i) => {
              const icons = [<Target size={22} />, <Users size={22} />, <Sparkles size={22} />, <BookOpen size={22} />, <MessageCircle size={22} />];
              const tids = ["home-svc-ads", "home-svc-privat", "home-svc-vip", "home-svc-course", "home-svc-wa"];
              const ext = /^https?:/.test(s.to || "");
              return (
                <ExtLink key={i} to={s.to} className={`home-svc-card${ext ? " home-svc-cta-card" : ""}`} data-testid={tids[i] || `home-svc-${i}`}>
                  {icons[i % icons.length]}
                  <b>{s.title}</b>
                  <p>{s.body}</p>
                  <span className="home-svc-cta">{s.cta} <ArrowRight size={14} /></span>
                </ExtLink>
              );
            })}
          </div>
        </section>
        <section className="section courses-section">
          <div className="section-head">
            <div>
              <span className="section-kicker">{t("courses.kicker")}</span>
              <h2>{rich(t("courses.title"))}</h2>
            </div>
            <Link
              to="/courses"
              className="text-link"
              data-testid="view-all-courses-link"
            >
              {t("courses.link")} <ArrowRight size={17} />
            </Link>
          </div>
          <div className="course-grid">
            {items.slice(0, 3).map((c, i) => (
              <CourseCard key={c.id} course={c} index={i} />
            ))}
          </div>
        </section>
        <section className="section value-band">
          <div className="value-grid">
            {list("value.items").map((f, i) => {
              const icons = [<Play size={22} />, <BookOpen size={22} />, <Award size={22} />];
              return (
              <div key={i} className="value-card">
                <div className="value-icon">{icons[i % icons.length]}</div>
                <b>{f.title}</b>
                <p>{f.body}</p>
              </div>
              );
            })}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

export function Courses() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  useSeo({
    title: "Katalog Kelas Digital Marketing",
    description: "Pilih kelas praktis Meta Ads, TikTok Ads, Shopee Ads, Landing Page, dan Fundamental Digital Marketing. Akses seumur hidup, bayar sekali.",
    path: "/courses",
    type: "website",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "Katalog Kelas MASTOFA.COM",
      description: "Kelas digital marketing berbasis praktik untuk seller, advertiser, dan bisnis.",
      inLanguage: "id-ID",
    },
  });
  useEffect(() => {
    api(`/courses?search=${encodeURIComponent(search)}`)
      .then(setItems)
      .catch(() => {});
  }, [search]);
  return (
    <div>
      <Header />
      <main className="page">
        <div className="page-intro">
          <span className="section-kicker">KATALOG KELAS</span>
          <h1>
            Temukan kelas untuk
            <br />
            <em>level berikutnya.</em>
          </h1>
          <p>
            Materi yang jelas, praktis, dan dirancang untuk hasil yang nyata.
          </p>
        </div>
        <div className="catalog-toolbar">
          <span>{items.length} kelas tersedia</span>
          <input
            data-testid="course-search-input"
            placeholder="Cari kelas..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="course-grid catalog-grid">
          {items.map((c, i) => (
            <CourseCard key={c.id} course={c} index={i % 9} />
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
}

export function Detail() {
  const { slug } = useParams();
  const [c, setC] = useState(null);
  const [preview, setPreview] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const nav = useNavigate();
  useSeo({
    title: c ? `${c.title} — Kelas ${c.category}` : "Detail Kelas",
    description: c ? c.description : "Detail kelas digital marketing di MASTOFA.COM.",
    path: `/courses/${slug}`,
    image: c?.thumbnail,
    type: "article",
    jsonLd: c
      ? (c.faq?.length
          ? { "@context": "https://schema.org", "@graph": [
              jsonLdCourse({ name: c.title, description: c.description, path: `/courses/${slug}`, price: c.price }),
              jsonLdFaq(c.faq),
            ] }
          : jsonLdCourse({ name: c.title, description: c.description, path: `/courses/${slug}`, price: c.price }))
      : undefined,
  });
  useEffect(() => {
    api(`/courses/${slug}`)
      .then((data) => {
        setC(data);
        if (data.preview_lesson_id) {
          api(`/courses/${slug}/preview`)
            .then((p) => setPreview(p.lesson))
            .catch(() => {});
        }
      })
      .catch(() => {});
  }, [slug]);
  if (!c) return <Loading label="Memuat kursus..." />;
  const moduleCount = c.modules?.length || 0;
  const lessonCount =
    c.modules?.reduce((n, m) => n + (m.lessons?.length || 0), 0) || 0;
  return (
    <div>
      <Header />
      <main className="detail-page">
        <Link to="/courses" className="back-link" data-testid="back-courses-link">
          ← Kembali ke katalog
        </Link>
        <section className="detail-hero">
          <div>
            <span className="pill">
              {c.level} · {c.category}
            </span>
            <h1>{c.title}</h1>
            <p>{c.description}</p>
            <div className="detail-facts">
              <span>
                <BookOpen size={17} /> {moduleCount} modul
              </span>
              <span>
                <Clock3 size={17} /> {lessonCount} materi
              </span>
              <span>
                <Users size={17} /> Akses seumur hidup
              </span>
            </div>
            {c.learning_objectives?.length ? (
              <div className="objectives">
                <b>Yang akan Anda pelajari</b>
                <ul>
                  {c.learning_objectives.map((o, i) => (
                    <li key={i}>
                      <CheckCircle2 size={15} /> {o}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
          <div className="buy-panel">
            <span>Investasi belajar</span>
            <strong>Rp {Number(c.price).toLocaleString("id-ID")}</strong>
            <button
              className="button full"
              data-testid="buy-course-button"
              onClick={() => {
                if (!localStorage.getItem("mastofa_token"))
                  nav(`/login?next=/checkout/${c.slug}`);
                else nav(`/checkout/${c.slug}`);
              }}
            >
              Belajar sekarang <ArrowRight size={17} />
            </button>
            {preview && (
              <button
                className="button-ghost"
                data-testid="preview-lesson-button"
                onClick={() => setShowPreview(true)}
                style={{ marginTop: 10, width: "100%", justifyContent: "center" }}
              >
                <Play size={15} /> Preview 1 lesson gratis
              </button>
            )}
            <small>Bayar sekali, akses selamanya</small>
          </div>
        </section>
        {showPreview && preview && (() => {
          const yt = preview.youtube_url?.match(/(?:v=|\/shorts\/|youtu\.be\/)([\w-]{6,})/)?.[1] || "";
          if (!yt) return null;
          return (
          <div className="modal" onClick={() => setShowPreview(false)}>
            <div className="modal-box" style={{ maxWidth: 780, padding: 0 }} onClick={(e) => e.stopPropagation()}>
              <div className="block-video">
                <iframe src={`https://www.youtube.com/embed/${yt}?autoplay=1`} title={preview.title} allowFullScreen />
              </div>
              <div style={{ padding: 24 }}>
                <h3 style={{ margin: 0, font: "700 20px Manrope" }}>Preview: {preview.title}</h3>
                <p style={{ color: "var(--muted)", fontSize: 14, marginTop: 10 }}>{preview.description}</p>
                <button className="button" onClick={() => setShowPreview(false)} style={{ marginTop: 14 }} data-testid="close-preview-button">Tutup</button>
              </div>
            </div>
          </div>
          );
        })()}
        <section className="curriculum">
          <div>
            <span className="section-kicker">KURIKULUM</span>
            <h2>Yang akan Anda pelajari</h2>
          </div>
          <div className="module-list">
            {c.modules?.map((m, i) => (
              <div
                className="module"
                key={m.id}
                data-testid={`detail-module-${m.id}`}
              >
                <div className="module-num">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div>
                  <b>{m.title}</b>
                  <p data-testid={`detail-module-lesson-count-${m.id}`}>
                    {m.lessons?.length || 0} materi pelajaran
                  </p>
                </div>
                <ChevronRight />
              </div>
            ))}
          </div>
        </section>
        {c.testimonials?.length ? (
          <section className="testimonial-section" data-testid="testimonial-section">
            <span className="section-kicker">CERITA MURID</span>
            <h2>Hasil nyata dari<br /><em>mereka yang belajar.</em></h2>
            <div className="testimonial-grid">
              {c.testimonials.map((t, i) => (
                <div key={i} className="testi-card">
                  <p>&ldquo;{t.quote}&rdquo;</p>
                  <div className="testi-meta">
                    <div className="avatar" style={{ background: "#eff6ff", color: "#2563eb" }}>
                      {(t.name || "M")[0]}
                    </div>
                    <div>
                      <b>{t.name}</b>
                      <small>{t.role}</small>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        ) : null}
        {c.faq?.length ? (
          <section className="course-faq" data-testid="course-faq-section">
            <span className="section-kicker">FAQ</span>
            <h2>Pertanyaan seputar kursus ini</h2>
            <div className="faq-list">
              {c.faq.map((f, i) => (
                <details key={i} className="faq-item">
                  <summary>{f.q}</summary>
                  <p>{f.a}</p>
                </details>
              ))}
            </div>
          </section>
        ) : null}
      </main>
      <Footer />
    </div>
  );
}

function Auth({ title, subtitle, children }) {
  return (
    <div className="auth-page">
      <Link to="/" className="auth-logo" data-testid="auth-logo">
        <img src={LOGO} alt="MASTOFA.COM" />
        <span>MASTOFA.COM</span>
      </Link>
      <div className="auth-box">
        <span className="section-kicker">MASTOFA ACADEMY</span>
        <h1>{title}</h1>
        <p>{subtitle}</p>
        {children}
      </div>
      <span className="auth-footer">© 2026 MASTOFA.COM · Belajar untuk bertumbuh</span>
    </div>
  );
}

export function Login() {
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [mode, setMode] = useState("password"); // password | magic
  const [magic, setMagic] = useState({ email: "", sent: false, sending: false });
  const nav = useNavigate();
  const nextUrl = new URLSearchParams(window.location.search).get("next") || "";
  const submit = async (e) => {
    e.preventDefault();
    try {
      const x = await api("/auth/login", { method: "POST", data: form });
      localStorage.setItem("mastofa_token", x.token);
      localStorage.setItem("mastofa_user", JSON.stringify(x.user));
      if (nextUrl && nextUrl.startsWith("/")) {
        nav(nextUrl, { replace: true });
      } else {
        nav(x.user.role === "ADMIN" ? "/admin" : "/dashboard");
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Tidak dapat masuk.");
    }
  };
  const sendMagic = async (e) => {
    e.preventDefault();
    setMagic({ ...magic, sending: true });
    try {
      await api("/auth/magic-link", { method: "POST", data: { email: magic.email } });
      setMagic({ ...magic, sent: true, sending: false });
    } catch (ex) {
      setError(ex.response?.data?.detail || "Gagal mengirim link.");
      setMagic({ ...magic, sending: false });
    }
  };
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const google = () => {
    const redirect = window.location.origin + "/auth/callback" + (nextUrl ? `?next=${encodeURIComponent(nextUrl)}` : "");
    window.location.href =
      "https://auth.emergentagent.com/?redirect=" + encodeURIComponent(redirect);
  };
  return (
    <Auth
      title="Selamat datang kembali"
      subtitle="Masuk untuk melanjutkan perjalanan belajar Anda."
    >
      <button
        type="button"
        onClick={google}
        className="google-btn"
        data-testid="google-login-button"
      >
        <img
          src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
          alt="Google"
        />
        Lanjut dengan Google
      </button>
      <div className="auth-divider"><span>atau pakai email</span></div>
      <div className="login-mode-tabs">
        <button
          type="button"
          className={mode === "password" ? "mode-tab active" : "mode-tab"}
          onClick={() => { setMode("password"); setError(""); }}
          data-testid="login-mode-password"
        >
          Password
        </button>
        <button
          type="button"
          className={mode === "magic" ? "mode-tab active" : "mode-tab"}
          onClick={() => { setMode("magic"); setError(""); }}
          data-testid="login-mode-magic"
        >
          Magic Link
        </button>
      </div>
      {mode === "password" ? (
        <form onSubmit={submit} className="auth-form">
          <label>
            Email
            <input
              data-testid="login-email-input"
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </label>
          <label>
            Password
            <input
              data-testid="login-password-input"
              type="password"
              required
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
            />
          </label>
          {error && (
            <div className="error" data-testid="login-error">
              {error}
            </div>
          )}
          <button className="button full" data-testid="login-submit-button">
            Masuk <ArrowRight size={17} />
          </button>
          <p className="auth-switch">
            <Link to="/forgot-password" data-testid="forgot-password-link">Lupa password?</Link>
          </p>
          <p className="auth-switch">
            Belum punya akun? <Link to="/register">Daftar sekarang</Link>
          </p>
        </form>
      ) : magic.sent ? (
        <div className="magic-sent" data-testid="magic-sent">
          <div className="check-icon" aria-hidden>✓</div>
          <h3>Cek email Anda</h3>
          <p>
            Jika <b>{magic.email}</b> terdaftar, kami sudah kirim link login yang berlaku
            <b> 15 menit</b>. Klik link di email untuk masuk otomatis tanpa password.
          </p>
          <p className="auth-switch">
            <button
              type="button"
              className="link-btn"
              onClick={() => setMagic({ email: "", sent: false, sending: false })}
              data-testid="magic-back"
            >
              ← Kirim ulang ke email lain
            </button>
          </p>
        </div>
      ) : (
        <form onSubmit={sendMagic} className="auth-form" data-testid="magic-form">
          <label>
            Email
            <input
              data-testid="magic-email-input"
              type="email"
              required
              placeholder="you@email.com"
              value={magic.email}
              onChange={(e) => setMagic({ ...magic, email: e.target.value })}
            />
          </label>
          <p className="hint" style={{ fontSize: 12, color: "var(--muted)", margin: "2px 0 10px" }}>
            Kami kirim link ke email — klik untuk login otomatis tanpa password.
          </p>
          {error && <div className="error" data-testid="magic-error">{error}</div>}
          <button className="button full" disabled={magic.sending} data-testid="magic-submit">
            {magic.sending ? "Mengirim..." : "Kirim Magic Link"} <ArrowRight size={17} />
          </button>
          <p className="auth-switch">
            Belum punya akun? <Link to="/register">Daftar sekarang</Link>
          </p>
        </form>
      )}
    </Auth>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api("/auth/forgot-password", { method: "POST", data: { email } });
      setSent(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Gagal mengirim. Coba lagi.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Auth title="Lupa password?" subtitle="Kami akan mengirim link reset password ke email Anda.">
      {sent ? (
        <div className="lead-sent" data-testid="forgot-sent" style={{ background: "#fff" }}>
          <CheckCircle2 size={36} />
          <b>Email terkirim</b>
          <p>
            Jika email <b>{email}</b> terdaftar, link reset password sudah dikirim.
            Periksa inbox &amp; folder spam Anda. Link berlaku 1 jam.
          </p>
          <Link to="/login" className="button">
            Kembali ke Login <ArrowRight size={16} />
          </Link>
        </div>
      ) : (
        <form onSubmit={submit} className="auth-form">
          <label>
            Email
            <input required type="email" value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="forgot-email-input" />
          </label>
          {error && <div className="error" data-testid="forgot-error">{error}</div>}
          <button className="button full" disabled={busy} data-testid="forgot-submit">
            {busy ? "Mengirim…" : "Kirim Link Reset"} <ArrowRight size={17} />
          </button>
          <p className="auth-switch">
            Ingat password? <Link to="/login">Masuk</Link>
          </p>
        </form>
      )}
    </Auth>
  );
}

export function MagicVerify() {
  const nav = useNavigate();
  const [state, setState] = useState("verifying"); // verifying | error
  const [error, setError] = useState("");
  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get("token");
    if (!token) {
      setState("error");
      setError("Link login tidak lengkap.");
      return;
    }
    api("/auth/magic-verify", { method: "POST", data: { token } })
      .then((x) => {
        localStorage.setItem("mastofa_token", x.token);
        localStorage.setItem("mastofa_user", JSON.stringify(x.user));
        nav(x.user.role === "ADMIN" ? "/admin" : "/dashboard", { replace: true });
      })
      .catch((ex) => {
        setState("error");
        setError(ex.response?.data?.detail || "Link login tidak valid atau kedaluwarsa.");
      });
  }, [nav]);
  return (
    <Auth title="Login otomatis" subtitle="Verifikasi link login Anda...">
      {state === "verifying" ? (
        <div className="magic-verifying" data-testid="magic-verifying">
          <div className="spinner" aria-hidden />
          <p>Sedang memverifikasi link Anda...</p>
        </div>
      ) : (
        <div className="magic-error-box" data-testid="magic-verify-error">
          <p className="error">{error}</p>
          <Link to="/login" className="button full" data-testid="magic-verify-back">
            Kembali ke Login <ArrowRight size={16} />
          </Link>
        </div>
      )}
    </Auth>
  );
}

export function ResetPassword() {
  const { token } = useParams();
  const nav = useNavigate();
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 8) return setError("Password minimal 8 karakter.");
    if (form.password !== form.confirm) return setError("Konfirmasi password tidak cocok.");
    setBusy(true);
    try {
      await api("/auth/reset-password", {
        method: "POST",
        data: { token, new_password: form.password },
      });
      setDone(true);
      setTimeout(() => nav("/login", { replace: true }), 2500);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Reset gagal.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Auth title="Buat password baru" subtitle="Masukkan password baru Anda.">
      {done ? (
        <div className="lead-sent" data-testid="reset-done" style={{ background: "#fff" }}>
          <CheckCircle2 size={36} />
          <b>Password berhasil di-reset</b>
          <p>Anda akan diarahkan ke halaman login…</p>
        </div>
      ) : (
        <form onSubmit={submit} className="auth-form">
          <label>
            Password baru
            <input required type="password" minLength="8"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              data-testid="reset-password-input" />
          </label>
          <label>
            Konfirmasi password
            <input required type="password" minLength="8"
              value={form.confirm}
              onChange={(e) => setForm({ ...form, confirm: e.target.value })}
              data-testid="reset-confirm-input" />
          </label>
          {error && <div className="error" data-testid="reset-error">{error}</div>}
          <button className="button full" disabled={busy} data-testid="reset-submit">
            {busy ? "Menyimpan…" : "Reset Password"} <ArrowRight size={17} />
          </button>
        </form>
      )}
    </Auth>
  );
}

export function Register() {
  const [form, setForm] = useState({ name: "", email: "", whatsapp: "", password: "", ref: "" });
  const [error, setError] = useState("");
  const nav = useNavigate();
  const nextUrl = new URLSearchParams(window.location.search).get("next") || "";
  useEffect(() => {
    const url = new URL(window.location.href);
    const ref = url.searchParams.get("ref") || localStorage.getItem("mastofa_ref") || "";
    if (ref) {
      localStorage.setItem("mastofa_ref", ref.toUpperCase());
      setForm((f) => ({ ...f, ref: ref.toUpperCase() }));
    }
  }, []);
  const submit = async (e) => {
    e.preventDefault();
    try {
      const x = await api("/auth/register", { method: "POST", data: form });
      localStorage.setItem("mastofa_token", x.token);
      localStorage.setItem("mastofa_user", JSON.stringify(x.user));
      window.mastofaTrack?.("CompleteRegistration", { method: "email" });
      if (nextUrl && nextUrl.startsWith("/")) {
        nav(nextUrl, { replace: true });
      } else {
        // Sprint 3: Kirim user baru ke onboarding wizard supaya dapat rekomendasi jalur belajar
        nav("/onboarding", { replace: true });
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Pendaftaran gagal.");
    }
  };
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const google = () => {
    const redirect = window.location.origin + "/auth/callback" + (nextUrl ? `?next=${encodeURIComponent(nextUrl)}` : "");
    window.location.href =
      "https://auth.emergentagent.com/?redirect=" + encodeURIComponent(redirect);
  };
  return (
    <Auth
      title="Mulai perjalanan Anda"
      subtitle="Satu akun untuk semua materi MASTOFA.COM."
    >
      <button
        type="button"
        onClick={google}
        className="google-btn"
        data-testid="google-register-button"
      >
        <img
          src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
          alt="Google"
        />
        Daftar cepat dengan Google
      </button>
      <div className="auth-divider"><span>atau pakai email</span></div>
      <form onSubmit={submit} className="auth-form">
        <label>
          Nama lengkap
          <input
            data-testid="register-name-input"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </label>
        <label>
          Email
          <input
            data-testid="register-email-input"
            type="email"
            required
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </label>
        <label>
          Nomor WhatsApp
          <input
            data-testid="register-whatsapp-input"
            type="tel"
            inputMode="numeric"
            required
            placeholder="628123456789 (tanpa +)"
            pattern="[0-9]{9,15}"
            value={form.whatsapp}
            onChange={(e) => setForm({ ...form, whatsapp: e.target.value.replace(/\D/g, "") })}
          />
          <small style={{ color: "#94a3b8", fontSize: 12 }}>
            Untuk info paket, undangan Zoom mentoring, dan konfirmasi resi.
          </small>
        </label>
        <label>
          Password
          <input
            data-testid="register-password-input"
            type="password"
            minLength="8"
            required
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
          />
        </label>
        {error && (
          <div className="error" data-testid="register-error">
            {error}
          </div>
        )}
        {form.ref && (
          <div
            style={{
              background: "#eff6ff",
              border: "1px solid #bfdbfe",
              padding: "10px 12px",
              borderRadius: 3,
              fontSize: 12,
              color: "#1e40af",
            }}
            data-testid="register-ref-badge"
          >
            🎁 Anda diundang oleh <b>{form.ref}</b> — dapatkan pengalaman terbaik!
          </div>
        )}
        <button className="button full" data-testid="register-submit-button">
          Buat akun <ArrowRight size={17} />
        </button>
        <p className="auth-switch">
          Sudah punya akun? <Link to="/login">Masuk</Link>
        </p>
      </form>
    </Auth>
  );
}

export function AuthCallback() {
  const nav = useNavigate();
  const processed = useRef(false);
  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = window.location.hash;
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      nav("/login", { replace: true });
      return;
    }
    const sid = match[1];
    (async () => {
      try {
        const r = await api("/auth/google/exchange", {
          method: "POST",
          headers: { "X-Session-ID": sid },
        });
        localStorage.setItem("mastofa_token", r.token);
        localStorage.setItem("mastofa_user", JSON.stringify(r.user));
        window.mastofaTrack?.("CompleteRegistration", { method: "google" });
        // Clean fragment
        window.history.replaceState({}, "", window.location.pathname);
        const nextUrl = new URLSearchParams(window.location.search).get("next");
        if (nextUrl && nextUrl.startsWith("/")) {
          nav(nextUrl, { replace: true });
        } else {
          nav(r.user.role === "ADMIN" ? "/admin" : "/dashboard", { replace: true });
        }
      } catch (e) {
        nav("/login?err=google", { replace: true });
      }
    })();
  }, [nav]);
  return (
    <div className="loading" data-testid="auth-callback-loading">
      Memproses login Google...
    </div>
  );
}

export function Marketing({ page }) {
  const [settings, setSettings] = useState({});
  const [bundles, setBundles] = useState([]);
  useEffect(() => {
    api("/settings/public").then(setSettings).catch(() => {});
    api("/bundles").then(setBundles).catch(() => {});
  }, []);
  const content = {
    pricing: {
      kicker: "HARGA",
      title: "Investasi belajar\nyang masuk akal.",
      body: "Bayar sekali, akses seumur hidup. Transfer Manual BCA (rekomendasi) atau pembayaran otomatis via Duitku.",
      details: (
        <>
          {bundles.length > 0 && (
            <div style={{ marginTop: 60 }}>
              <span className="section-kicker">PAKET HEMAT</span>
              <h2 style={{ font: "800 32px Manrope", margin: "10px 0 30px", letterSpacing: "-0.03em" }}>
                Bundle populer.
              </h2>
              <div className="bundle-grid">
                {bundles.map((b) => (
                  <div key={b.id} className="bundle-card" data-testid={`public-bundle-${b.id}`}>
                    {b.regular_price > b.price && (
                      <span className="savings">
                        HEMAT Rp {Number(b.regular_price - b.price).toLocaleString("id-ID")}
                      </span>
                    )}
                    <span>PAKET HEMAT</span>
                    <h3>{b.title}</h3>
                    <p>{b.description}</p>
                    <div>
                      {b.regular_price > b.price && (
                        <span className="strike">
                          Rp {Number(b.regular_price).toLocaleString("id-ID")}
                        </span>
                      )}
                    </div>
                    <strong>Rp {Number(b.price).toLocaleString("id-ID")}</strong>
                    <ul>
                      {b.courses.map((c) => (
                        <li key={c.slug}>
                          <CheckCircle2 size={14} /> {c.title}
                        </li>
                      ))}
                    </ul>
                    <Link
                      to={`/checkout-bundle/${b.id}`}
                      className="button"
                      data-testid={`bundle-buy-${b.id}`}
                    >
                      Ambil paket ini
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div style={{ marginTop: 60 }}>
            <span className="section-kicker">SATUAN</span>
            <h2 style={{ font: "800 32px Manrope", margin: "10px 0 30px", letterSpacing: "-0.03em" }}>
              Beli kelas satuan.
            </h2>
          </div>
          <div className="pricing-grid">
            <div className="pricing-card">
              <span>PAKET DASAR</span>
              <h3>Fundamental Digital Marketing</h3>
              <strong>Rp 249.000</strong>
              <p>Materi pondasi dari nol hingga siap menjalankan campaign.</p>
              <Link to="/courses/fundamental-digital-marketing" className="button">
                Ambil kursus
              </Link>
            </div>
            <div className="pricing-card featured">
              <span>PALING POPULER</span>
              <h3>Kelas Advanced Ads</h3>
              <strong>Rp 499.000</strong>
              <p>Setiap kelas iklan (Shopee, TikTok, Meta) bisa dibeli terpisah.</p>
              <Link to="/courses" className="button">
                Lihat semua kelas
              </Link>
            </div>
            <div className="pricing-card">
              <span>KUSTOM</span>
              <h3>Pelatihan Tim</h3>
              <strong>Hubungi kami</strong>
              <p>Kurikulum kustom untuk in-house team & agency partner.</p>
              <Link to="/contact" className="button">
                Konsultasi
              </Link>
            </div>
          </div>
        </>
      ),
    },
    about: {
      kicker: "TENTANG",
      title: "Tentang\nMASTOFA.COM",
      body:
        settings.about ||
        "MASTOFA.COM adalah platform pembelajaran digital marketing praktis untuk seller, advertiser, dan pemilik bisnis di Indonesia.",
    },
    contact: {
      kicker: "KONTAK",
      title: "Butuh bantuan?\nHubungi kami.",
      body: `Email: ${settings.contact_email || "halo@mastofa.com"}`,
    },
    faq: {
      kicker: "FAQ",
      title: "Pertanyaan yang\nsering diajukan.",
      body: "",
      details: (
        <div className="faq-list">
          {(settings.faq || []).map((f, i) => (
            <details key={i} className="faq-item">
              <summary>{f.q}</summary>
              <p>{f.a}</p>
            </details>
          ))}
        </div>
      ),
    },
    terms: {
      kicker: "SYARAT & KETENTUAN",
      title: "Syarat Layanan",
      body: "Dengan menggunakan MASTOFA.COM Anda menyetujui bahwa materi digital tidak dapat dikembalikan setelah akses aktif. Kami menjamin akses seumur hidup selama platform beroperasi.",
    },
    privacy: {
      kicker: "PRIVASI",
      title: "Kebijakan Privasi",
      body: `MASTOFA.COM ("kami") menghormati privasi setiap pengunjung dan member. Halaman ini menjelaskan bagaimana data Anda dikumpulkan, digunakan, dan dilindungi.

1. Data yang Kami Kumpulkan
Kami menyimpan data yang Anda berikan secara sukarela ketika mendaftar akun, melakukan pembayaran, atau menghubungi kami: nama lengkap, alamat email, nomor WhatsApp, alamat pengiriman (jika membeli produk fisik), serta riwayat transaksi dan progres belajar.

2. Cara Kami Menggunakan Data
Data digunakan untuk (a) menyediakan akses kursus/mentoring/produk yang Anda beli, (b) mengirim konfirmasi pembayaran dan invoice, (c) menghubungi Anda terkait jadwal Zoom mentoring atau invite link Telegram VIP, (d) analitik internal untuk memperbaiki layanan.

3. Berbagi ke Pihak Ketiga
Kami tidak menjual data Anda ke pihak ketiga. Data hanya dibagikan ke penyedia layanan tepercaya yang membantu operasional platform: Duitku (pemrosesan pembayaran), Emergent Object Storage (penyimpanan file media), Biteship (pengiriman produk fisik), Telegram Bot API (akses grup VIP), Resend (email transaksional), dan penyedia analitik pemasaran (Meta Pixel, TikTok Pixel, Google Analytics 4). Data yang dikirim ke pixel tracking di-hash SHA-256 sebelum diproses (email/phone) sesuai standar Advanced Matching Meta & TikTok.

4. Keamanan Data
Password disimpan menggunakan bcrypt. JWT session dienkripsi dengan secret server. Koneksi ke website menggunakan HTTPS/TLS. Kami menyimpan data secukupnya sesuai kebutuhan layanan.

5. Hak Anda
Anda dapat meminta ekspor data, koreksi, atau penghapusan akun sepenuhnya dengan menghubungi Admin WhatsApp: 0851-5700-9099.

6. Cookies & Pelacakan
Website menggunakan cookies teknis (session, preferensi bahasa) dan cookies analitik (Meta Pixel, TikTok Pixel, GA4) untuk mengukur kinerja iklan. Anda dapat menonaktifkan cookies via pengaturan browser.

7. Perubahan Kebijakan
Kebijakan ini dapat diperbarui sewaktu-waktu tanpa pemberitahuan sebelumnya. Perubahan berlaku sejak dipublikasikan di halaman ini.

8. Kontak
PT MASTOFA DIGITAL / MASTOFA.COM
Jl. Soekarno Hatta No. 381, Bugangin, Kendal Kota, Kab. Kendal, Jawa Tengah, 51314
WhatsApp: 0851-5700-9099
Email: support@mastofa.com

Terakhir diperbarui: Februari 2026.`,
    },
  }[page];
  return (
    <div>
      <Header />
      <main className="page">
        <div className="page-intro">
          <span className="section-kicker">{content.kicker}</span>
          <h1 style={{ whiteSpace: "pre-line" }}>{content.title}</h1>
          <p style={{ whiteSpace: "pre-line" }}>{content.body}</p>
        </div>
        {content.details}
      </main>
      <Footer />
    </div>
  );
}
